import { useEffect, useRef, useState } from 'react';
import './header.css';
import moriLogo from '../assets/images/mori_logo-removebg-preview.png';

const NORTHERN_CIRCUIT_LINKS = [
  { label: 'Serengeti', href: '#serengeti' },
  { label: 'Ngorongoro', href: '#ngorongoro' },
  { label: 'Tarangire', href: '#tarangire' },
  { label: 'Lake Manyara', href: '#manyara' },
  { label: 'Arusha National Park', href: '#arusha-park' },
];

const SOUTHERN_CIRCUIT_LINKS = [
  { label: 'Mikumi', href: '#mikumi' },
  { label: 'Nyerere', href: '#nyerere' },
  { label: 'Ruaha', href: '#ruaha' },
];

const ISLAND_LINKS = [
  { label: 'Zanzibar', href: '#zanzibar' },
];

const TREKKING_LINKS = [
  { label: 'Mount Kilimanjaro', href: '#kilimanjaro' },
  { label: 'Mount Meru', href: '#meru' },
];

const NAV_LINKS = [
  { label: 'Safaris', href: '#safaris' },
  { label: 'Packages', href: '#packages' },
  { label: 'About', href: '#about' },
  { label: 'Contact', href: '#contact' },
];

const DESTINATION_SUBMENUS = [
  {
    id: 'northern-circuit-menu',
    headingId: 'northern-circuits-heading',
    label: 'Northern Circuits',
    links: NORTHERN_CIRCUIT_LINKS,
    stateKey: 'northern',
  },
  {
    id: 'southern-circuit-menu',
    headingId: 'southern-circuits-heading',
    label: 'Southern Circuits',
    links: SOUTHERN_CIRCUIT_LINKS,
    stateKey: 'southern',
  },
  {
    id: 'island-destinations-menu',
    headingId: 'island-destinations-heading',
    label: 'Islands',
    links: ISLAND_LINKS,
    stateKey: 'island',
  },
];

const LANGUAGES = [
  { code: 'en', label: 'English', short: 'EN', flag: 'gb' },
  { code: 'sw', label: 'Kiswahili', short: 'SW', flag: 'tz' },
  { code: 'fr', label: 'Français', short: 'FR', flag: 'fr' },
  { code: 'de', label: 'Deutsch', short: 'DE', flag: 'de' },
  { code: 'es', label: 'Español', short: 'ES', flag: 'es' },
  { code: 'it', label: 'Italiano', short: 'IT', flag: 'it' },
  { code: 'pt', label: 'Português', short: 'PT', flag: 'pt' },
  { code: 'nl', label: 'Nederlands', short: 'NL', flag: 'nl' },
  { code: 'ar', label: 'العربية', short: 'AR', flag: 'sa' },
  { code: 'zh', label: '中文', short: 'ZH', flag: 'cn' },
  { code: 'ru', label: 'Русский', short: 'RU', flag: 'ru' },
  { code: 'ja', label: '日本語', short: 'JA', flag: 'jp' },
];

const LANG_STORAGE_KEY = 'mori-lang';

// Map our language codes to Google Translate language codes
const GOOGLE_LANG = {
  en: 'en',
  sw: 'sw',
  fr: 'fr',
  de: 'de',
  es: 'es',
  it: 'it',
  pt: 'pt',
  nl: 'nl',
  ar: 'ar',
  zh: 'zh-CN',
  ru: 'ru',
  ja: 'ja',
};

// Persist the chosen language in the cookie the Google Translate widget reads on load
function setGoogleTranslateCookie(googleCode) {
  const value = `/en/${googleCode}`;
  const host = window.location.hostname;
  document.cookie = `googtrans=${value};path=/`;
  if (host) {
    document.cookie = `googtrans=${value};path=/;domain=${host}`;
    document.cookie = `googtrans=${value};path=/;domain=.${host}`;
  }
}

const FLAG_TITLES = {
  gb: 'United Kingdom',
  tz: 'Tanzania',
  fr: 'France',
  de: 'Germany',
  es: 'Spain',
  it: 'Italy',
  pt: 'Portugal',
  nl: 'Netherlands',
  sa: 'Saudi Arabia',
  cn: 'China',
  ru: 'Russia',
  jp: 'Japan',
};

function FlagSvg({ className, title, children }) {
  return (
    <svg
      className={className}
      viewBox="0 0 60 40"
      width="20"
      height="14"
      aria-hidden="true"
      focusable="false"
    >
      <title>{title}</title>
      {children}
    </svg>
  );
}

function LanguageFlag({ code, className = '' }) {
  const title = FLAG_TITLES[code] || 'Flag';

  if (code === 'gb') {
    return (
      <FlagSvg className={className} title={title}>
        <rect width="60" height="40" fill="#012169" />
        <path d="M0 0 L60 40 M60 0 L0 40" stroke="#fff" strokeWidth="8" />
        <path d="M0 0 L60 40 M60 0 L0 40" stroke="#C8102E" strokeWidth="5" />
        <path d="M30 0 V40 M0 20 H60" stroke="#fff" strokeWidth="13" />
        <path d="M30 0 V40 M0 20 H60" stroke="#C8102E" strokeWidth="7" />
      </FlagSvg>
    );
  }

  if (code === 'tz') {
    return (
      <FlagSvg className={className} title={title}>
        <rect width="60" height="40" fill="#1eb53a" />
        <path d="M0 40 L60 0 V40 Z" fill="#00a3dd" />
        <path d="M0 40 L60 0" stroke="#fcd116" strokeWidth="10" fill="none" />
        <path d="M0 40 L60 0" stroke="#000" strokeWidth="6" fill="none" />
      </FlagSvg>
    );
  }

  if (code === 'fr') {
    return (
      <FlagSvg className={className} title={title}>
        <rect width="20" height="40" fill="#002395" />
        <rect x="20" width="20" height="40" fill="#fff" />
        <rect x="40" width="20" height="40" fill="#ED2939" />
      </FlagSvg>
    );
  }

  if (code === 'de') {
    return (
      <FlagSvg className={className} title={title}>
        <rect width="60" height="40" fill="#000" />
        <rect y="13.33" width="60" height="13.34" fill="#D00" />
        <rect y="26.67" width="60" height="13.33" fill="#FFCE00" />
      </FlagSvg>
    );
  }

  if (code === 'es') {
    return (
      <FlagSvg className={className} title={title}>
        <rect width="60" height="40" fill="#AA151B" />
        <rect y="10" width="60" height="20" fill="#F1BF00" />
      </FlagSvg>
    );
  }

  if (code === 'it') {
    return (
      <FlagSvg className={className} title={title}>
        <rect width="20" height="40" fill="#009246" />
        <rect x="20" width="20" height="40" fill="#fff" />
        <rect x="40" width="20" height="40" fill="#CE2B37" />
      </FlagSvg>
    );
  }

  if (code === 'pt') {
    return (
      <FlagSvg className={className} title={title}>
        <rect width="24" height="40" fill="#006600" />
        <rect x="24" width="36" height="40" fill="#FF0000" />
        <circle cx="24" cy="20" r="7" fill="#FFD700" stroke="#fff" strokeWidth="1" />
      </FlagSvg>
    );
  }

  if (code === 'nl') {
    return (
      <FlagSvg className={className} title={title}>
        <rect width="60" height="40" fill="#21468B" />
        <rect y="13.33" width="60" height="13.34" fill="#fff" />
        <rect y="26.67" width="60" height="13.33" fill="#AE1C28" />
      </FlagSvg>
    );
  }

  if (code === 'sa') {
    return (
      <FlagSvg className={className} title={title}>
        <rect width="60" height="40" fill="#006C35" />
        <rect x="8" y="17" width="44" height="6" fill="#fff" opacity="0.92" />
      </FlagSvg>
    );
  }

  if (code === 'cn') {
    return (
      <FlagSvg className={className} title={title}>
        <rect width="60" height="40" fill="#DE2910" />
        <polygon
          points="12,8 13.8,13.2 19.4,13.2 14.8,16.5 16.6,22 12,18.7 7.4,22 9.2,16.5 4.6,13.2 10.2,13.2"
          fill="#FFDE00"
        />
      </FlagSvg>
    );
  }

  if (code === 'ru') {
    return (
      <FlagSvg className={className} title={title}>
        <rect width="60" height="40" fill="#D52B1E" />
        <rect y="13.33" width="60" height="13.34" fill="#0039A6" />
        <rect y="26.67" width="60" height="13.33" fill="#fff" />
      </FlagSvg>
    );
  }

  if (code === 'jp') {
    return (
      <FlagSvg className={className} title={title}>
        <rect width="60" height="40" fill="#fff" />
        <circle cx="30" cy="20" r="9" fill="#BC002D" />
      </FlagSvg>
    );
  }

  return (
    <FlagSvg className={className} title={title}>
      <rect width="60" height="40" fill="#d9d9d9" />
    </FlagSvg>
  );
}

const getInitialLanguage = () => {
  if (typeof window === 'undefined') return LANGUAGES[0];
  const saved = window.localStorage.getItem(LANG_STORAGE_KEY);
  return LANGUAGES.find((lang) => lang.code === saved) || LANGUAGES[0];
};

function Header() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [destOpen, setDestOpen] = useState(false);
  const [openDestSubmenu, setOpenDestSubmenu] = useState(null);
  const [trekOpen, setTrekOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [language, setLanguage] = useState(getInitialLanguage);
  const [suppressDropdownHover, setSuppressDropdownHover] = useState(false);
  const destRef = useRef(null);
  const trekRef = useRef(null);
  const langRef = useRef(null);
  const barRef = useRef(null);

  const isDesktopNav = () => window.matchMedia('(min-width: 769px)').matches;

  const closeDestSubmenus = () => setOpenDestSubmenu(null);

  const isDestSubmenuOpen = (stateKey) => openDestSubmenu === stateKey;

  const openDestSubmenuOnly = (stateKey) => setOpenDestSubmenu(stateKey);

  const toggleDestSubmenu = (stateKey) => {
    setOpenDestSubmenu((current) => (current === stateKey ? null : stateKey));
  };

  const closeMenu = () => {
    setMenuOpen(false);
    setDestOpen(false);
    closeDestSubmenus();
    setTrekOpen(false);
    setLangOpen(false);
  };

  const clearDropdownHover = () => {
    setSuppressDropdownHover(false);
    setDestOpen(false);
    closeDestSubmenus();
    setTrekOpen(false);
  };

  const applyLanguage = (next) => {
    if (next.code === language.code) {
      setLangOpen(false);
      return;
    }
    setLanguage(next);
    setLangOpen(false);
    window.localStorage.setItem(LANG_STORAGE_KEY, next.code);
    document.documentElement.lang = next.code;
    window.dispatchEvent(new CustomEvent('mori:languagechange', { detail: next }));

    // Apply site-wide translation via Google Translate, then reload so the
    // widget re-renders every page section in the selected language.
    setGoogleTranslateCookie(GOOGLE_LANG[next.code] || 'en');
    window.location.reload();
  };

  const handleNavClick = (href, { fromDropdown = false } = {}) => (event) => {
    closeMenu();
    if (fromDropdown) {
      setSuppressDropdownHover(true);
      event.currentTarget.blur();
    }
    if (href === '/') {
      event.preventDefault();
      if (window.location.hash) {
        window.location.hash = '';
      }
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  useEffect(() => {
    document.documentElement.lang = language.code;
  }, [language.code]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (destRef.current && !destRef.current.contains(event.target)) {
        setDestOpen(false);
        closeDestSubmenus();
      }
      if (trekRef.current && !trekRef.current.contains(event.target)) {
        setTrekOpen(false);
      }
      if (langRef.current && !langRef.current.contains(event.target)) {
        setLangOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    document.body.classList.toggle('nav-locked', menuOpen);
    return () => document.body.classList.remove('nav-locked');
  }, [menuOpen]);

  useEffect(() => {
    const bar = barRef.current;
    if (!bar) return undefined;

    const syncHeaderOffset = () => {
      const isMobile = window.matchMedia('(max-width: 768px)').matches;
      if (!isMobile) {
        document.documentElement.style.removeProperty('--site-header-offset');
        return;
      }
      const height = Math.ceil(bar.getBoundingClientRect().height);
      document.documentElement.style.setProperty('--site-header-offset', `${height}px`);
    };

    syncHeaderOffset();
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(syncHeaderOffset) : null;
    observer?.observe(bar);
    window.addEventListener('resize', syncHeaderOffset);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', syncHeaderOffset);
      document.documentElement.style.removeProperty('--site-header-offset');
    };
  }, []);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') closeMenu();
    };
    const handleResize = () => {
      if (window.matchMedia('(min-width: 769px)').matches) closeMenu();
    };
    document.addEventListener('keydown', handleKeyDown);
    window.addEventListener('resize', handleResize);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <header className="site-header site-header--enter">
      <div className="site-header__bar" ref={barRef}>
        <div className="site-header__inner">
          <a className="site-header__brand" href="/" onClick={handleNavClick('/')}>
            <span className="site-header__logo" aria-hidden="true">
              <img
                className="site-header__logo-img"
                src={moriLogo}
                alt=""
                width={88}
                height={88}
                decoding="async"
              />
            </span>
            <span className="site-header__brand-text">
              <span className="site-header__name">Mori Tours</span>
              <span className="site-header__tagline">Safari &amp; Travel</span>
            </span>
          </a>

          <nav
            id="site-header-nav"
            className={`site-header__nav ${menuOpen ? 'site-header__nav--open' : ''}`}
            aria-label="Main navigation"
          >
            <ul className="site-header__links">
              <li>
                <a href="/" onClick={handleNavClick('/')}>
                  Home
                </a>
              </li>

              <li
                ref={destRef}
                className={`site-header__dropdown${destOpen ? ' site-header__dropdown--open' : ''}${
                  suppressDropdownHover ? ' site-header__dropdown--hover-suppressed' : ''
                }`}
                onMouseEnter={() => {
                  if (isDesktopNav()) setDestOpen(true);
                }}
                onMouseLeave={() => {
                  if (isDesktopNav()) clearDropdownHover();
                }}
              >
                <button
                  type="button"
                  className="site-header__dropdown-toggle"
                  aria-expanded={destOpen}
                  aria-haspopup="true"
                  aria-controls="destinations-menu"
                  onClick={() => {
                    if (!isDesktopNav()) setDestOpen((open) => !open);
                  }}
                >
                  Destinations
                  <span className="site-header__dropdown-chevron" aria-hidden="true" />
                </button>
                <ul id="destinations-menu" className="site-header__dropdown-menu">
                  {DESTINATION_SUBMENUS.map((submenu) => {
                    const submenuOpen = isDestSubmenuOpen(submenu.stateKey);

                    return (
                      <li
                        key={submenu.stateKey}
                        className={`site-header__subdropdown${
                          submenuOpen ? ' site-header__subdropdown--open' : ''
                        }`}
                        onMouseEnter={() => {
                          if (isDesktopNav()) openDestSubmenuOnly(submenu.stateKey);
                        }}
                        onMouseLeave={() => {
                          if (isDesktopNav()) closeDestSubmenus();
                        }}
                      >
                        <button
                          type="button"
                          className="site-header__subdropdown-toggle"
                          id={submenu.headingId}
                          aria-expanded={submenuOpen}
                          aria-haspopup="true"
                          aria-controls={submenu.id}
                          onClick={() => {
                            if (!isDesktopNav()) toggleDestSubmenu(submenu.stateKey);
                          }}
                        >
                          {submenu.label}
                          <span
                            className="site-header__subdropdown-chevron"
                            aria-hidden="true"
                          />
                        </button>
                        <ul id={submenu.id} className="site-header__subdropdown-menu">
                          {submenu.links.map((link) => (
                            <li key={link.href}>
                              <a
                                href={link.href}
                                onClick={handleNavClick(link.href, { fromDropdown: true })}
                              >
                                {link.label}
                              </a>
                            </li>
                          ))}
                        </ul>
                      </li>
                    );
                  })}
                </ul>
              </li>

              <li
                ref={trekRef}
                className={`site-header__dropdown${trekOpen ? ' site-header__dropdown--open' : ''}${
                  suppressDropdownHover ? ' site-header__dropdown--hover-suppressed' : ''
                }`}
                onMouseEnter={() => {
                  if (isDesktopNav()) setTrekOpen(true);
                }}
                onMouseLeave={() => {
                  if (isDesktopNav()) {
                    setSuppressDropdownHover(false);
                    setTrekOpen(false);
                  }
                }}
              >
                <button
                  type="button"
                  className="site-header__dropdown-toggle"
                  aria-expanded={trekOpen}
                  aria-haspopup="true"
                  aria-controls="trekking-menu"
                  onClick={() => {
                    if (!isDesktopNav()) setTrekOpen((open) => !open);
                  }}
                >
                  Trekking
                  <span className="site-header__dropdown-chevron" aria-hidden="true" />
                </button>
                <ul id="trekking-menu" className="site-header__dropdown-menu">
                  {TREKKING_LINKS.map((link) => (
                    <li key={link.href}>
                      <a
                        href={link.href}
                        onClick={handleNavClick(link.href, { fromDropdown: true })}
                      >
                        {link.label}
                      </a>
                    </li>
                  ))}
                </ul>
              </li>

              {NAV_LINKS.map((link) => (
                <li key={link.href}>
                  <a href={link.href} onClick={handleNavClick(link.href)}>
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
            <a className="site-header__cta" href="#contact" onClick={closeMenu}>
              Plan your trip
            </a>
          </nav>

          <div
            ref={langRef}
            className={`site-header__lang${langOpen ? ' site-header__lang--open' : ''}`}
          >
            <button
              type="button"
              className="site-header__lang-toggle"
              aria-expanded={langOpen}
              aria-haspopup="listbox"
              aria-controls="site-header-lang-menu"
              aria-label={`Language: ${language.label}`}
              onClick={() => setLangOpen((open) => !open)}
            >
              <LanguageFlag code={language.flag} className="site-header__lang-flag" />
              <span className="site-header__lang-code notranslate" translate="no" aria-hidden="true">
                {language.short}
              </span>
              <span className="site-header__lang-chevron" aria-hidden="true" />
            </button>
            <ul
              id="site-header-lang-menu"
              className="site-header__lang-menu"
              role="listbox"
              aria-label="Select language"
            >
              {LANGUAGES.map((lang) => {
                const selected = lang.code === language.code;
                return (
                  <li key={lang.code} role="presentation">
                    <button
                      type="button"
                      role="option"
                      aria-selected={selected}
                      className={`site-header__lang-option${
                        selected ? ' site-header__lang-option--active' : ''
                      }`}
                      onClick={() => applyLanguage(lang)}
                    >
                      <LanguageFlag
                        code={lang.flag}
                        className="site-header__lang-option-flag"
                      />
                      <span className="site-header__lang-option-label">{lang.label}</span>
                      <span className="site-header__lang-option-code notranslate" translate="no">
                        {lang.short}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>

          <button
            type="button"
            className="site-header__toggle"
            aria-expanded={menuOpen}
            aria-controls="site-header-nav"
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            onClick={() => setMenuOpen((open) => !open)}
          >
            <span className="site-header__toggle-bar" />
            <span className="site-header__toggle-bar" />
            <span className="site-header__toggle-bar" />
          </button>
        </div>
      </div>
    </header>
  );
}

export default Header;
